/*
 * Admin Payment Confirmation Recovery · Feb 2026 · Batch 13A V1.7
 * ================================================================
 *
 * Quiet operator surface listing payment-confirmation SMS intents
 * that failed or were intentionally suppressed at queue time.
 *
 * Truth-boundary wording rules:
 *   • "Retry dispatch"        — button intent only.
 *   • "Retrying…"             — while the request is active.
 *   • "Dispatch queued for retry" — ONLY when backend
 *                                    `status === "dispatched"`.
 *   • "Still suppressed by preferences" — `status === "suppressed"`.
 *   • "Retry failed · <msg>"  — `status === "failed"`.
 * Never claims that the SMS reached the customer or that the provider accepted it.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCcw, AlertOctagon } from "lucide-react";
import { useTruthState, TRUTH_STATE_NEEDS_INPUT } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL || "";

const RETRYABLE_STATUSES = ["dispatch_failed", "dispatch_suppressed"];

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function statusChip(status) {
  const map = {
    dispatch_failed: { label: "Failed", tone: "bg-rose-100 text-rose-800" },
    dispatch_suppressed: { label: "Suppressed", tone: "bg-amber-100 text-amber-800" },
  };
  const it = map[status] || { label: status, tone: "bg-slate-100 text-slate-800" };
  return (
    <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${it.tone}`}>
      {it.label}
    </span>
  );
}

export default function AdminPaymentConfirmations() {
  const [rows, setRows] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [inflight, setInflight] = useState({});
  // Batch P1 · Operations Truth-State — canonical async receipt for
  // every operator action. `Dispatch queued` / `Retry in progress`
  // map to `waiting` (SMS not delivered yet — provider callback is
  // the authoritative boundary). Explicit `dispatch_failed` /
  // `not_retryable` / `suppressed` map to `needs_input`.
  const truth = useTruthState();
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const r = await axios.get(
        `${API}/api/operator/payment-confirmations/failed`,
        { withCredentials: true },
      );
      setRows(Array.isArray(r?.data?.items) ? r.data.items : []);
      setLoadState("ok");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (intentId) => {
    if (inflight[intentId]) return;
    setInflight((s) => ({ ...s, [intentId]: true }));
    try {
      await truth.run(
        async () => {
          const r = await axios.post(
            `${API}/api/operator/payment-confirmations/${intentId}/retry`,
            {},
            { withCredentials: true },
          );
          const status = r?.data?.status;
          if (status === "dispatched") {
            // Provider accepted the request. Delivery is NOT
            // authoritative until the provider callback lands.
            truth.setState(
              "waiting",
              "Dispatch queued — SMS provider accepted the request. Delivery evidence still awaits provider callback.",
            );
          } else if (status === "suppressed") {
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              r?.data?.dispatch_skip_reason || "Still suppressed by learner notification preferences.",
            );
          } else if (status === "failed") {
            truth.setState(
              "failed",
              r?.data?.dispatch_error || "Retry failed — provider rejected the request.",
            );
          } else if (status === "skipped") {
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              r?.data?.dispatch_skip_reason || "Dispatch skipped — no dispatch path available.",
            );
          } else if (status === "not_retryable") {
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              `Not retryable — current status: ${r?.data?.current_status || "unknown"}.`,
            );
          } else {
            truth.setState("failed", status || "Retry returned an unknown outcome.");
          }
          await load();
          return r?.data;
        },
        {
          failedMessage: "Retry failed — network or server error.",
        },
      );
    } catch { /* truth.run captured; TruthReceipt shows Retry */ }
    setInflight((s) => { const n = { ...s }; delete n[intentId]; return n; });
  };

  return (
    <div data-testid="admin-payment-confirmations" className="min-h-dvh bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <AlertOctagon className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">Payment Confirmation Recovery</h1>
            <p className="text-xs text-slate-500">
              Intents that failed or were suppressed at queue time. Retry re-runs the canonical dispatch — it does not touch payment records or Stripe.
            </p>
          </div>
          <button
            data-testid="payment-confirmations-reload"
            onClick={load}
            className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100"
            aria-label="Reload"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </header>

        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={() => truth.setState(null)}
          onDismiss={() => truth.setState(null)}
        />

        {loadState === "loading" && (
          <p data-testid="payment-confirmations-loading" className="text-sm text-slate-500">Loading unresolved intents…</p>
        )}
        {loadState === "error" && (
          <p data-testid="payment-confirmations-load-error" className="text-sm text-rose-600">
            Couldn&apos;t load the recovery lane. Please refresh to try again.
          </p>
        )}
        {loadState === "ok" && rows.length === 0 && (
          <p data-testid="payment-confirmations-empty" className="text-sm text-slate-500">No unresolved payment-confirmation intents.</p>
        )}

        {loadState === "ok" && rows.length > 0 && (
          <ul className="space-y-3" data-testid="payment-confirmations-list">
            {rows.map((row) => {
              const busy = !!inflight[row.intent_id];
              const eligible = RETRYABLE_STATUSES.includes(row.status);
              return (
                <li
                  key={row.intent_id}
                  data-testid={`payment-confirmation-row-${row.intent_id}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 dark:text-white truncate">
                          {row.learner_name || "Unknown learner"}
                        </p>
                        {statusChip(row.status)}
                        <span className="text-[10px] text-slate-500">· {row.payment_type || "payment"}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">
                        {row.intent_id}
                      </p>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                        <dt className="text-slate-500">Created</dt>
                        <dd className="text-slate-900 dark:text-white text-right">{fmtWhen(row.created_at)}</dd>
                        <dt className="text-slate-500">Phone</dt>
                        <dd className="text-slate-900 dark:text-white text-right font-mono">{row.phone_number || "—"}</dd>
                        <dt className="text-slate-500">Retry count</dt>
                        <dd className="text-slate-900 dark:text-white text-right">{row.retry_count}</dd>
                        <dt className="text-slate-500">Last retry</dt>
                        <dd className="text-slate-900 dark:text-white text-right">{fmtWhen(row.last_retry_at)}</dd>
                      </dl>
                      {row.dispatch_skip_reason && (
                        <p
                          data-testid={`payment-confirmation-skip-${row.intent_id}`}
                          className="text-xs mt-2 text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded"
                        >
                          Suppression / skip: {row.dispatch_skip_reason}
                        </p>
                      )}
                      {row.last_error && (
                        <p
                          data-testid={`payment-confirmation-error-${row.intent_id}`}
                          className="text-xs mt-2 text-rose-700 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded"
                        >
                          Last error: {row.last_error}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`payment-confirmation-retry-${row.intent_id}`}
                      onClick={() => retry(row.intent_id)}
                      disabled={busy || !eligible}
                      className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      {busy ? "Retrying…" : "Retry dispatch"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
